import {
  Alert,
  Flex,
  Heading,
  IconInfoSolid,
  Tabs,
  Text,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { Actions } from "../../../../components/Action/Actions";
import { Card } from "../../../../components/Card";
import {
  ERROR,
  useNotification,
} from "../../../../components/CustomHooks/NotificationProvider";
import { ContextReflections } from "../../../../components/Reflection/ContextReflections";
import ChartData from "../../../../sections/ChartData";
import { AssessmentPlanService } from "../../../../services/AssessmentService";
import AssignmentRubric from "../Components/AssignmentRubric";

const AssignmentView = ({
  currentSection,
  reflectionTemplates,
  sectionRefTemplate,
  dashboardFilters,
  setDashboardFilters,
  trigger,
  setTrigger,
  selectedAssignment,
  chartLoad,
  setChartLoad,
}) => {
  const showNotification = useNotification();
  const [tabIndex, setTabIndex] = useState(0);
  const [isAssessed, setIsAssessed] = useState(false);
  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    async function fetchAssessment() {
      try {
        setIsAssessed(true);
        const assessment = await AssessmentPlanService.getAssessmentById(
          selectedAssignment.assignment.assessment_id
        );
        setAssessment(assessment);
      } catch {
        showNotification(
          t(
            "notifications.general.fetchError",
            { obj: "assessment data" },
            ERROR
          )
        );
      }
    }

    if (selectedAssignment.assignment?.plan_id !== "-1") {
      fetchAssessment();
    } else {
      setAssessment({});
    }
  }, [selectedAssignment, showNotification]);

  return (
    <Flex alignItems="start" wrap="wrap" gap="medium">
      <Flex.Item size="100%">
        <ChartData
          showLineChart
          showBarChart={false}
          inCourse
          isFacultyDash
          filters={dashboardFilters}
          setFilters={setDashboardFilters}
          trigger={trigger}
          setTrigger={setTrigger}
          section={currentSection}
          assignmentData={selectedAssignment}
          chartLoad={chartLoad}
          setChartLoad={setChartLoad}
          courseID={currentSection.canvas_id}
        />
      </Flex.Item>
      <Flex.Item size="100%">
        <Card
          style={{
            height: 600,
            position: "relative",
            border:
              !currentSection.blueprint && isAssessed
                ? "1px solid green"
                : "none",
          }}
        >
          {!currentSection.blueprint && isAssessed && (
            <label
              style={{
                display: "block",
                position: "absolute",
                top: "-10px",
                left: "15px",
                width: "auto",
                backgroundColor: "#fff",
                padding: "0 5px",
                fontSize: "0.9rem",
                color: "green",
              }}
            >
              {t("dashboard.associatedToAssessment")}
            </label>
          )}
          <Flex justifyItems="space-between" gap="small" alignItems="start">
            <Flex.Item shouldShrink shouldGrow size="48%">
              <Heading as="h3" level="h4" margin="0 0 small 0">
                {selectedAssignment?.assignment?.name ? (
                  `${t("dashboard.scoresAndRubricsFor")}: ${
                    selectedAssignment.assignment.name
                  }`
                ) : (
                  <>
                    <Text>{`${t("dashboard.scoresAndRubricsFor")}: ${t(
                      "dashboard.unplannedAssignment"
                    )} `}</Text>
                    <Tooltip
                      renderTip={
                        <div style={{ width: "600px" }}>
                          {t("dashboard.unplannedDisclaimer")}
                        </div>
                      }
                      placement="top"
                      on={["click", "hover", "focus"]}
                      color="primary-inverse"
                    >
                      <IconInfoSolid color="brand" />
                    </Tooltip>
                  </>
                )}
              </Heading>
              <div style={{ maxHeight: 570, overflowY: "auto" }}>
                {selectedAssignment["outcome-0"] ? (
                  <AssignmentRubric
                    assignmentData={selectedAssignment}
                    assessment={assessment}
                  />
                ) : (
                  <Alert variant="info">
                    {t("dashboard.noResultsCollected")}
                  </Alert>
                )}
              </div>
            </Flex.Item>
            <Flex.Item>
              <div style={{ height: 600, borderRight: "1px solid #ccc" }} />
            </Flex.Item>
            <Flex.Item shouldShrink shouldGrow size="48%">
              <Tabs onRequestTabChange={(_e, { index }) => setTabIndex(index)}>
                <Tabs.Panel
                  id="reflections-faculty"
                  renderTitle={t("reflections.reflections")}
                  isSelected={tabIndex === 0}
                  padding="small 0"
                >
                  <div style={{ maxHeight: 525, padding: "0 10px" }}>
                    <ContextReflections
                      contextId={currentSection.id}
                      contextName={currentSection.name}
                      reflectionTemplates={reflectionTemplates}
                      sectionRefTemplate={sectionRefTemplate}
                    />
                  </div>
                </Tabs.Panel>
                <Tabs.Panel
                  id="actions-faculty"
                  renderTitle={t("actions.actions")}
                  isSelected={tabIndex === 1}
                >
                  <div style={{ maxHeight: 525, padding: "0 10px" }}>
                    <Actions contextId={currentSection.id} />
                  </div>
                </Tabs.Panel>
              </Tabs>
            </Flex.Item>
          </Flex>
        </Card>
      </Flex.Item>
    </Flex>
  );
};

export default AssignmentView;
